import { ensureArray, ensureBoolean, ensureString, ensureFiniteNumber } from 'ensure-type';

var isBlankString = function isBlankString(str) {
  if (typeof str !== 'string') {
    return false;
  }
  if (str.length === 0) {
    return true;
  }
  return isWhitespace(str);
};
var isEmptyArray = function isEmptyArray(value) {
  return Array.isArray(value) && value.length === 0;
};
var isEmptyObject = function isEmptyObject(value) {
  return !isNullOrUndefined(value) && Object.keys(value).length === 0 && value.constructor === Object;
};
var isNullOrUndefined = function isNullOrUndefined(value) {
  return value === null || value === undefined;
};
var isWhitespace = function isWhitespace(value) {
  // @see https://github.com/jonschlinkert/whitespace-regex
  // eslint-disable-next-line no-control-regex
  var whitespaceRegex = /^[\s\f\n\r\t\u1680\u180e\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200a\u2028\u2029\u202f\u205f\u3000\ufeff\x09\x0a\x0b\x0c\x0d\x20\xa0]+$/;
  return typeof value === 'string' && whitespaceRegex.test(value);
};

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _typeof(obj) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  }, _typeof(obj);
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

var canUseDOM = function canUseDOM() {
  return !!(typeof window !== 'undefined' && window.document && window.document.createElement);
};
var contains = function () {
  // https://developer.mozilla.org/en-US/docs/Web/API/Node/compareDocumentPosition
  var DOCUMENT_POSITION_CONTAINED_BY = 16;
  var fallback = function fallback(context, node) {
    if (node) {
      do {
        if (node === context) {
          return true;
        }
      } while (node = node.parentNode);
    }
    return false;
  };

  // HTML DOM and SVG DOM may have different support levels,
  // so we need to check on context instead of a document root element.
  return function (context, node) {
    if (!canUseDOM) {
      return fallback(context, node);
    }
    if (context.contains) {
      return context.contains(node);
    }
    if (context.compareDocumentPosition) {
      return context === node || !!(context.compareDocumentPosition(node) & DOCUMENT_POSITION_CONTAINED_BY); // eslint-disable-line no-bitwise
    }

    return fallback(context, node);
  };
}();
var getActiveElement = function getActiveElement(node) {
  var doc = getOwnerDocument(node);
  return doc === null || doc === void 0 ? void 0 : doc.activeElement;
};
var getComputedStyle = function getComputedStyle(node) {
  if (!node) {
    throw new TypeError('No element passed to `getComputedStyle()`');
  }
  var doc = node.ownerDocument;
  if ('defaultView' in doc) {
    if (doc.defaultView.opener) {
      return node.ownerDocument.defaultView.getComputedStyle(node, null);
    }
    return window.getComputedStyle(node, null);
  }
  return null;
};
var getEventWindow = function getEventWindow(event) {
  var _event$view;
  return (_event$view = event === null || event === void 0 ? void 0 : event.view) !== null && _event$view !== void 0 ? _event$view : window;
};
var getOwnerDocument = function getOwnerDocument(node) {
  var _node$ownerDocument;
  return isElement(node) ? (_node$ownerDocument = node.ownerDocument) !== null && _node$ownerDocument !== void 0 ? _node$ownerDocument : document : document;
};
var getOwnerWindow = function getOwnerWindow(node) {
  var _getOwnerDocument$def, _getOwnerDocument;
  return isElement(node) ? (_getOwnerDocument$def = (_getOwnerDocument = getOwnerDocument(node)) === null || _getOwnerDocument === void 0 ? void 0 : _getOwnerDocument.defaultView) !== null && _getOwnerDocument$def !== void 0 ? _getOwnerDocument$def : window : window;
};
var getRelatedTarget = function getRelatedTarget(event) {
  var _event$target, _event$relatedTarget;
  var target = (_event$target = event.target) !== null && _event$target !== void 0 ? _event$target : event.currentTarget;
  var activeElement = getActiveElement(target);
  return (_event$relatedTarget = event.relatedTarget) !== null && _event$relatedTarget !== void 0 ? _event$relatedTarget : activeElement;
};
var isElement = function isElement(el) {
  return el !== null && el !== undefined && _typeof(el) === 'object' && 'nodeType' in el && el.nodeType === Node.ELEMENT_NODE;
};
var isHTMLElement = function isHTMLElement(el) {
  var _el$ownerDocument$def;
  if (!isElement(el)) {
    return false;
  }
  var win = (_el$ownerDocument$def = el.ownerDocument.defaultView) !== null && _el$ownerDocument$def !== void 0 ? _el$ownerDocument$def : window;
  return el instanceof win.HTMLElement;
};

/**
 * Get the normalized event key across all browsers
 * @param event keyboard event
 */
var normalizeKeyboardEventKey = function normalizeKeyboardEventKey(event) {
  var key = event.key,
    keyCode = event.keyCode;
  var isArrowKey = keyCode >= 37 && keyCode <= 40 && key.indexOf('Arrow') !== 0;
  var eventKey = isArrowKey ? "Arrow".concat(key) : key;
  return eventKey;
};
var reflow = function reflow(node) {
  return node && (node === null || node === void 0 ? void 0 : node.scrollTop);
};

var focusableElementSelectors = ['a[href]', 'area[href]', 'audio[controls]', 'button:not([disabled])', 'embed', 'iframe', 'input:not([type="hidden"]):not([disabled])', 'object', 'select:not([disabled])', 'textarea:not([disabled])', 'video[controls]', '*[contenteditable]:not([contenteditable="false"])', '*[tabindex]:not([aria-disabled="true"])'];
var focusableElementSelector = focusableElementSelectors.join(',');
var getAllFocusable = function getAllFocusable(element) {
  var keyboardOnly = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var focusableElements = Array.from(element.querySelectorAll(focusableElementSelector));

  // Filter out elements with `display: none`
  focusableElements = focusableElements.filter(function (focusableElement) {
    var computedStyle = getComputedStyle(focusableElement);
    return (computedStyle === null || computedStyle === void 0 ? void 0 : computedStyle.display) !== 'none';
  });
  if (keyboardOnly === true) {
    focusableElements = focusableElements.filter(function (focusableElement) {
      return focusableElement.getAttribute('tabindex') !== '-1';
    });
  }
  return focusableElements;
};

var _joinWords = function _joinWords(words) {
  words = ensureArray(words);
  if (words.length === 0) {
    return '';
  }
  if (words.length === 1) {
    return "'".concat(words[0], "'");
  }
  if (words.length === 2) {
    return "'".concat(words[0], "' and '").concat(words[1], "'");
  }
  return "'".concat(words.slice(0, -1).join('\', \''), "', and '").concat(words.slice(-1), "'");
};
var ariaAttr = function ariaAttr(condition) {
  return condition ? true : undefined;
};
var callAll = function callAll() {
  for (var _len = arguments.length, fns = new Array(_len), _key = 0; _key < _len; _key++) {
    fns[_key] = arguments[_key];
  }
  return function mergedFn() {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }
    fns.forEach(function (fn) {
      fn === null || fn === void 0 ? void 0 : fn.apply(void 0, args);
    });
  };
};
var callEventHandlers = function callEventHandlers() {
  for (var _len3 = arguments.length, fns = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
    fns[_key3] = arguments[_key3];
  }
  return function mergedFn(event) {
    fns.some(function (fn) {
      fn === null || fn === void 0 ? void 0 : fn(event);
      return event === null || event === void 0 ? void 0 : event.defaultPrevented;
    });
  };
};
var dataAttr = function dataAttr(condition) {
  return condition ? '' : undefined;
};
var isFunction = function isFunction(value) {
  return typeof value === 'function';
};
var noop = function noop() {};
var once = function once(fn) {
  var called = false;
  var result;
  return function () {
    if (!called) {
      result = fn.apply(void 0, arguments);
      called = true;
    }
    return result;
  };
};
var runIfFn = function runIfFn(valueOrFn) {
  for (var _len4 = arguments.length, args = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
    args[_key4 - 1] = arguments[_key4];
  }
  return isFunction(valueOrFn) ? valueOrFn.apply(void 0, args) : valueOrFn;
};
var warnDeprecatedProps = function warnDeprecatedProps(props, options) {
  var _options$prefix;
  var prefix = (_options$prefix = options === null || options === void 0 ? void 0 : options.prefix) !== null && _options$prefix !== void 0 ? _options$prefix : 'Warning:';
  var alternative = ensureArray(options === null || options === void 0 ? void 0 : options.alternative);
  var willRemove = ensureBoolean(options === null || options === void 0 ? void 0 : options.willRemove);
  var message = ensureString(options === null || options === void 0 ? void 0 : options.message);
  props = ensureArray(props);
  if (props.length === 0) {
    return;
  }
  var messages = [prefix];
  var verb = props.length > 1 ? 'are' : 'is';
  if (willRemove) {
    messages.push("".concat(_joinWords(props), " ").concat(verb, " deprecated and will be removed in the next major release."));
  } else {
    messages.push("".concat(_joinWords(props), " ").concat(verb, " deprecated."));
  }
  if (alternative.length > 0) {
    messages.push("Use ".concat(_joinWords(alternative), " instead."));
  }
  if (message) {
    messages.push(message);
  }
  console.error(messages.join(' '));
};
var warnRemovedProps = function warnRemovedProps(props, options) {
  var _options$prefix2;
  var prefix = (_options$prefix2 = options === null || options === void 0 ? void 0 : options.prefix) !== null && _options$prefix2 !== void 0 ? _options$prefix2 : 'Warning:';
  var alternative = ensureArray(options === null || options === void 0 ? void 0 : options.alternative);
  var message = ensureString(options === null || options === void 0 ? void 0 : options.message);
  props = ensureArray(props);
  if (props.length === 0) {
    return;
  }
  var messages = [prefix];
  var verb = props.length > 1 ? 'are' : 'is';
  messages.push("".concat(_joinWords(props), " ").concat(verb, " removed."));
  if (alternative.length > 0) {
    messages.push("Use ".concat(_joinWords(alternative), " instead."));
  }
  if (message) {
    messages.push(message);
  }
  console.error(messages.join(' '));
};

var formatMs = function formatMs(ms) {
  return ms > 0 ? "".concat(Math.round(ms), "ms") : '';
};
var transitionDuration = {
  // most basic recommended timing
  standard: 300,
  // recommended when something is entering screen
  enteringScreen: 225,
  // recommended when something is leaving screen
  leavingScreen: 195
};
var transitionEasing = {
  // This is the most common easing curve.
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  // Objects enter the screen at full velocity from off-screen and slowly decelerate to a resting point.
  easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
  // Objects leave the screen at full velocity. They do not decelerate when off-screen.
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  // The sharp curve is used by objects that may return to the screen at any time.
  sharp: 'cubic-bezier(0.4, 0, 0.6, 1)'
};
var getEnterTransitionProps = function getEnterTransitionProps(_ref) {
  var _timeout$enter, _timeout, _easing$enter, _easing, _style$transitionDura, _style$transitionTimi;
  var style = _ref.style,
    timeout = _ref.timeout,
    easing = _ref.easing;
  timeout = (_timeout$enter = (_timeout = timeout) === null || _timeout === void 0 ? void 0 : _timeout.enter) !== null && _timeout$enter !== void 0 ? _timeout$enter : timeout;
  easing = (_easing$enter = (_easing = easing) === null || _easing === void 0 ? void 0 : _easing.enter) !== null && _easing$enter !== void 0 ? _easing$enter : easing;
  return {
    duration: (_style$transitionDura = style === null || style === void 0 ? void 0 : style.transitionDuration) !== null && _style$transitionDura !== void 0 ? _style$transitionDura : typeof timeout === 'number' ? ensureFiniteNumber(timeout) : timeout || 0,
    easing: (_style$transitionTimi = style === null || style === void 0 ? void 0 : style.transitionTimingFunction) !== null && _style$transitionTimi !== void 0 ? _style$transitionTimi : easing,
    delay: style === null || style === void 0 ? void 0 : style.transitionDelay
  };
};
var getExitTransitionProps = function getExitTransitionProps(_ref2) {
  var _timeout$exit, _timeout2, _easing$exit, _easing2, _style$transitionDura2, _style$transitionTimi2;
  var style = _ref2.style,
    timeout = _ref2.timeout,
    easing = _ref2.easing;
  timeout = (_timeout$exit = (_timeout2 = timeout) === null || _timeout2 === void 0 ? void 0 : _timeout2.exit) !== null && _timeout$exit !== void 0 ? _timeout$exit : timeout;
  easing = (_easing$exit = (_easing2 = easing) === null || _easing2 === void 0 ? void 0 : _easing2.exit) !== null && _easing$exit !== void 0 ? _easing$exit : easing;
  return {
    duration: (_style$transitionDura2 = style === null || style === void 0 ? void 0 : style.transitionDuration) !== null && _style$transitionDura2 !== void 0 ? _style$transitionDura2 : typeof timeout === 'number' ? ensureFiniteNumber(timeout) : timeout || 0,
    easing: (_style$transitionTimi2 = style === null || style === void 0 ? void 0 : style.transitionTimingFunction) !== null && _style$transitionTimi2 !== void 0 ? _style$transitionTimi2 : easing,
    delay: style === null || style === void 0 ? void 0 : style.transitionDelay
  };
};
var createTransitionStyle = function createTransitionStyle(props, options) {
  var _props;
  props = (_props = props) !== null && _props !== void 0 ? _props : ['all'];
  var _options = _objectSpread2({}, options),
    _options$duration = _options.duration,
    duration = _options$duration === void 0 ? transitionDuration.standard : _options$duration,
    _options$easing = _options.easing,
    easing = _options$easing === void 0 ? transitionEasing.easeInOut : _options$easing,
    _options$delay = _options.delay,
    delay = _options$delay === void 0 ? 0 : _options$delay;
  return ensureArray(props).map(function (transitionProp) {
    var parts = [transitionProp, typeof duration === 'string' ? duration : formatMs(duration), easing, typeof delay === 'string' ? delay : formatMs(delay)].filter(function (x) {
      return x !== undefined && x !== null && x !== '';
    });
    return parts.join(' ');
  }).join(',');
};

export { ariaAttr, callAll, callEventHandlers, canUseDOM, contains, createTransitionStyle, dataAttr, getActiveElement, getAllFocusable, getComputedStyle, getEnterTransitionProps, getEventWindow, getExitTransitionProps, getOwnerDocument, getOwnerWindow, getRelatedTarget, isBlankString, isElement, isEmptyArray, isEmptyObject, isFunction, isHTMLElement, isNullOrUndefined, isWhitespace, noop, normalizeKeyboardEventKey, once, reflow, runIfFn, transitionDuration, transitionEasing, warnDeprecatedProps, warnRemovedProps };
